import React from 'react';
import { theme } from '../theme';
import DropPinWordmark from './DropPinWordmark';

// v0.57.0 - Suite SSO auto-login splash. Shown for the brief moment while the
// session is checked and, signed out, Synerflo ID is asked (v0.246.0 — it was
// July's shared insite_sso cookie before). Branded, on-theme (design tokens only), motion-reduced aware.
// Copy is fixed per Brett: "Signing you in to DropPin by Synerflo".
//
// Pure presentational: no data, no effects. AppShell mounts it in place of the
// login screen while the silent sign-in is in flight, then swaps to the app
// (success) or the normal LoginScreen (no suite session).

// Matches the DropPinWordmark "i" / canvas pushpin head colour.
const PIN_HEAD = '#fb923c';

export default function SsoSplash() {
  return (
    <div style={wrap} role="status" aria-live="polite" aria-busy="true">
      <style>{CSS}</style>

      {/* Soft radial "map" backdrop, brand-tinted, tokens only. */}
      <div className="sso-backdrop" aria-hidden />

      <div style={{ position: 'relative', textAlign: 'center', maxWidth: 440, padding: 24 }}>
        <div className="sso-stage" aria-hidden>
          <div className="sso-pin"><Pin /></div>
          <div className="sso-ripple" />
          <div className="sso-ripple sso-ripple-2" />
          <div className="sso-shadow" />
        </div>

        <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.01em', marginTop: 6 }}>
          <DropPinWordmark />
        </div>

        <h1 style={{ fontSize: 17, fontWeight: 700, color: theme.text, margin: '16px 0 4px' }}>
          Signing you in to DropPin by Synerflo
        </h1>
        <p style={{ fontSize: 13, color: theme.textLight, margin: 0, lineHeight: 1.5 }}>
          One InSite sign-in, every app. Getting your workspace ready&hellip;
        </p>

        <div className="sso-bar" aria-hidden><div className="sso-bar-fill" /></div>
      </div>
    </div>
  );
}

// Scaled standalone pushpin (same glyph as the wordmark "i" / plan-canvas pin).
function Pin() {
  return (
    <svg viewBox="0 0 40 60" width="60" height="90" style={{ overflow: 'visible', display: 'block' }}>
      <line x1="12" y1="55" x2="22" y2="24" stroke="#5b6470" strokeWidth="2.6" strokeLinecap="round" />
      <line x1="12.5" y1="54" x2="21.5" y2="24" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" />
      <circle cx="22" cy="18" r="12" fill={PIN_HEAD} stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
      <ellipse cx="18" cy="13" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.55)" />
    </svg>
  );
}

const wrap = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.bg,
  color: theme.text,
  overflow: 'hidden',
};

// Keyframes + decorative classes. Scoped by the sso- prefix. Colours reference
// design tokens; the pin head matches the established brand pin glyph.
const CSS = `
.sso-backdrop{
  position:absolute; inset:0;
  background:
    radial-gradient(60% 60% at 50% 38%, var(--brand-50) 0%, transparent 60%),
    radial-gradient(120% 90% at 50% 120%, var(--surface-2) 0%, transparent 55%);
  opacity:.9;
}
.sso-stage{ position:relative; height:120px; display:flex; align-items:flex-end; justify-content:center; }
.sso-pin{
  transform-origin:50% 100%;
  animation: sso-drop 1.5s cubic-bezier(.2,0,0,1) infinite;
}
@keyframes sso-drop{
  0%   { transform: translateY(-46px) rotate(-9deg); opacity:0; }
  30%  { transform: translateY(0)      rotate(0);    opacity:1; }
  40%  { transform: translateY(-7px); }
  52%  { transform: translateY(0); }
  60%,100% { transform: translateY(0); opacity:1; }
}
.sso-shadow{
  position:absolute; bottom:8px; left:50%; width:30px; height:7px; border-radius:50%;
  background:rgba(15,23,42,.20); filter:blur(.5px);
  transform:translateX(-50%) scale(.4);
  animation: sso-shadow 1.5s cubic-bezier(.2,0,0,1) infinite;
}
@keyframes sso-shadow{
  0%  { transform:translateX(-50%) scale(.4); opacity:.05; }
  30% { transform:translateX(-50%) scale(1);  opacity:.22; }
  100%{ transform:translateX(-50%) scale(1);  opacity:.18; }
}
.sso-ripple{
  position:absolute; bottom:9px; left:50%; width:14px; height:14px; border-radius:50%;
  border:2px solid var(--brand-600);
  transform:translateX(-50%) scale(.3); opacity:0;
  animation: sso-ripple 1.5s ease-out infinite;
}
.sso-ripple-2{ animation-delay:.2s; border-color:var(--brand-500); }
@keyframes sso-ripple{
  0%,30% { transform:translateX(-50%) scale(.3); opacity:0; }
  42%    { opacity:.5; }
  100%   { transform:translateX(-50%) scale(3.4); opacity:0; }
}
.sso-bar{
  margin:20px auto 0; width:190px; height:4px; border-radius:999px;
  background:var(--surface-2); overflow:hidden;
}
.sso-bar-fill{
  height:100%; width:42%; border-radius:999px;
  background:linear-gradient(90deg, var(--brand-500), var(--brand-700));
  animation: sso-slide 1.25s cubic-bezier(.2,0,0,1) infinite;
}
@keyframes sso-slide{
  0%   { transform:translateX(-120%); }
  100% { transform:translateX(320%); }
}
@media (prefers-reduced-motion: reduce){
  .sso-pin,.sso-ripple,.sso-shadow,.sso-bar-fill{ animation:none !important; }
  .sso-pin{ opacity:1 !important; transform:none !important; }
  .sso-ripple{ display:none; }
  .sso-bar-fill{ width:100%; opacity:.6; }
}
`;
